import { omit } from 'lodash';
import type { HierarchicalItem, HierarchicalItemMenu, MenuItem, TreeItem } from '@martech-solution/utils/types';
import { v4 as uuid } from 'uuid';

type HierarchyType<T> = {
  id: number | string;
  parentId?: number | string | null;
  level?: number;
  children?: T[];
};

export function buildHierarchyMenu(
  flatArray: HierarchicalItemMenu[] | undefined,
  currentResourceId?: number
): MenuItem[] | undefined {
  if (flatArray) {
    const map = new Map<string, HierarchicalItemMenu>();
    const roots: HierarchicalItemMenu[] = [];

    flatArray?.forEach((node) => {
      const { id, parentId, ...rest } = node;
      const newNode: HierarchicalItemMenu = { id, ...rest };

      map.set(id.toString(), newNode);

      if (!parentId || currentResourceId === parentId) {
        roots.push(newNode);
      } else {
        const parentNode = map.get(parentId.toString());

        if (parentNode) {
          if (!parentNode.children) {
            parentNode.children = [];
          }

          parentNode.children.push(newNode);
        }
      }
    });
    const menuItems: MenuItem[] = roots?.sort((a, b) => a?.sort - b?.sort)?.map((item) => convertToMenuItem(item));
    return menuItems;
  }
}

export function buildHierarchySelect(
  flatArray: HierarchicalItem[] | undefined,
  isDisableParent?: boolean
): TreeItem[] | undefined {
  if (flatArray) {
    const map = new Map<string, HierarchicalItem>();
    const roots: HierarchicalItem[] = [];

    flatArray?.forEach((node) => {
      const { id, parentId, ...rest } = node;
      const newNode: HierarchicalItem = { id, ...rest };

      map.set(id.toString(), newNode);

      if (!parentId) {
        roots.push(newNode);
      } else {
        const parentNode = map.get(parentId.toString());

        if (parentNode) {
          if (!parentNode.children) {
            parentNode.children = [];
          }

          parentNode.children.push(newNode);
        }
      }
    });
    const treeItems: TreeItem[] = roots.map((item) => convertToTreeItem(item, isDisableParent));
    return treeItems;
  }
}

function convertToTreeItem(item: HierarchicalItem, isDisableParent?: boolean): TreeItem {
  const data = omit(item, ['children', 'parentId']);
  const treeItem: TreeItem = {
    key: item.id,
    value: item.id.toString(),
    title: item.name,
    data,
    disabled: isDisableParent ? !!item?.children && item.children.length > 0 : false,
    children: []
  };

  if (item.children && item.children.length > 0) {
    treeItem.children = item.children.map((child) => convertToTreeItem(child));
  }

  return treeItem;
}

function convertToMenuItem(item: HierarchicalItemMenu): MenuItem {
  const menuItem: MenuItem = {
    key: item.path || `undefined ${uuid()}`,
    // label: React.createElement(Text, { children: item.name, title: item.name }),
    label: item.name,
    disabled: !(item.children.length > 0) && !item.path,
    // icon: item?.icon,
    children: []
  };

  if (item.children && item.children.length > 0) {
    menuItem.children = item.children?.sort((a, b) => a?.sort - b?.sort)?.map((child) => convertToMenuItem(child));
  } else {
    menuItem.children = undefined;
  }

  return menuItem;
}

export function buildHierarchy<T extends HierarchyType<T>>(flatArray: T[] | undefined, root: string): T[] | undefined {
  if (!flatArray) {
    return undefined;
  }

  if (flatArray.length === 0) {
    return [];
  }

  // Filter out items with path = root
  const filteredArray = flatArray.filter((item) => {
    // Check if item has a path property and if it equals root
    const itemWithPath = item as T & { path?: string };
    return itemWithPath.path !== root;
  });

  const map = new Map<string, T>();
  const roots: T[] = [];

  // First pass: Create all nodes in the map with children array
  filteredArray.forEach((node) => {
    const nodeWithChildren = { ...node, children: [], level: 0 } as T;
    map.set(String(node.id), nodeWithChildren);
  });

  // Second pass: Build the hierarchy
  filteredArray.forEach((node) => {
    const currentNode = map.get(String(node.id));
    if (!currentNode) return;

    // Check if node has a parent
    if (node.parentId !== null && node.parentId !== undefined && node.parentId !== '') {
      const parentNode = map.get(String(node.parentId));

      if (parentNode) {
        // Set level based on parent
        currentNode.level = (parentNode.level || 0) + 1;

        // Ensure parent has children array
        if (!parentNode.children) {
          parentNode.children = [];
        }

        // Add current node to parent's children
        parentNode.children.push(currentNode);
      } else {
        // Parent not found, treat as root (orphan)
        currentNode.level = 0;
        roots.push(currentNode);
      }
    } else {
      // No parent, this is a root node
      currentNode.level = 0;
      roots.push(currentNode);
    }
  });

  return roots;
}

export function findMenuItem(menuItems: MenuItem[], key: string): MenuItem | undefined {
  for (const item of menuItems) {
    if (item.key === key) {
      return item;
    }
    if (item.children) {
      const found = findMenuItem(item.children, key);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
}
